import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { uploadSupportMedia } from '../documents/blocks/blockUtils';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Smile } from 'lucide-react';
import { SUPPORT_EMOJIS } from '../../lib/supportConstants';
import { cn } from '../../lib/utils';
import { resolveUploadAssetUrl } from '../../lib/resolveUploadAssetUrl';

const TOOLBAR_OPTIONS = [
  [{ header: [2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote', 'code-block'],
  ['link', 'image', 'video', 'attachment'],
  ['clean'],
];

const icons = Quill.import('ui/icons');
icons.video =
  '<svg viewbox="0 0 18 18"><polygon class="ql-fill" points="4,3 14,9 4,15"></polygon></svg>';
icons.attachment =
  '<svg viewbox="0 0 18 18"><path class="ql-stroke" d="M12.5 2.5h-5A2.5 2.5 0 0 0 5 5v8a3.5 3.5 0 0 0 7 0V6.5a1.5 1.5 0 0 0-3 0V11a.5.5 0 0 1-1 0V6.5a2.5 2.5 0 0 1 5 0V13a4.5 4.5 0 0 1-9 0V5a4 4 0 0 1 8 0v7.5a3 3 0 0 1-6 0V7"></path></svg>';

function destroyQuill(quill, hostEl) {
  if (!quill) return;
  try {
    const toolbar = quill.getModule('toolbar');
    if (toolbar?.container?.parentNode) {
      toolbar.container.remove();
    }
  } catch {
    /* ignore */
  }
  if (hostEl) {
    hostEl.className = 'support-quill-host';
    hostEl.innerHTML = '';
  }
}

const SupportMessageEditor = forwardRef(function SupportMessageEditor(
  {
    orgId,
    placeholder = 'Describe your request…',
    readOnly = false,
    expanded = true,
    onReady,
  },
  ref
) {
  const hostRef = useRef(null);
  const quillRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const orgIdRef = useRef(orgId);
  const onReadyRef = useRef(onReady);
  orgIdRef.current = orgId;
  onReadyRef.current = onReady;

  useImperativeHandle(ref, () => ({
    async save() {
      const quill = quillRef.current;
      if (!quill) return { html: '' };
      return { html: quill.root.innerHTML };
    },
    async clear() {
      const quill = quillRef.current;
      if (!quill) return;
      quill.setContents([]);
    },
    async isEmpty() {
      const quill = quillRef.current;
      if (!quill) return true;
      const html = quill.root.innerHTML;
      const text = quill.getText().trim();
      if (text) return false;
      return !/<(img|video|a)\b/i.test(html);
    },
    focus() {
      quillRef.current?.focus();
    },
  }));

  useEffect(() => {
    const hostEl = hostRef.current;
    if (!hostEl || readOnly) return undefined;

    const imageHandler = () => imageInputRef.current?.click();
    const videoHandler = () => videoInputRef.current?.click();
    const attachmentHandler = () => fileInputRef.current?.click();

    const quill = new Quill(hostEl, {
      theme: 'snow',
      placeholder,
      modules: {
        toolbar: {
          container: TOOLBAR_OPTIONS,
          handlers: {
            image: imageHandler,
            video: videoHandler,
            attachment: attachmentHandler,
          },
        },
      },
    });

    quillRef.current = quill;
    onReadyRef.current?.();

    return () => {
      destroyQuill(quill, hostEl);
      quillRef.current = null;
    };
  }, [placeholder, readOnly]);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    quill.enable(!readOnly);
  }, [readOnly]);

  const uploadAndInsert = async (file, kind) => {
    const org = orgIdRef.current;
    if (!org) throw new Error('Organization is required for uploads.');
    return uploadSupportMedia(org, file, kind);
  };

  const insertEmoji = (emoji) => {
    const quill = quillRef.current;
    if (!quill) return;
    const range = quill.getSelection(true);
    const index = range?.index ?? Math.max(0, quill.getLength() - 1);
    quill.insertText(index, emoji, 'user');
    quill.setSelection(index + emoji.length);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !quillRef.current) return;
    try {
      const res = await uploadAndInsert(file, 'image');
      if (!res?.url) return;
      const quill = quillRef.current;
      const range = quill.getSelection(true);
      const index = range?.index ?? Math.max(0, quill.getLength() - 1);
      quill.insertEmbed(index, 'image', resolveUploadAssetUrl(res.url));
      quill.setSelection(index + 1);
    } catch (err) {
      console.error(err);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !quillRef.current) return;
    try {
      const res = await uploadAndInsert(file, 'video');
      if (!res?.url) return;
      const url = resolveUploadAssetUrl(res.url);
      const quill = quillRef.current;
      const range = quill.getSelection(true);
      const index = range?.index ?? Math.max(0, quill.getLength() - 1);
      quill.clipboard.dangerouslyPasteHTML(
        index,
        `<p><video controls class="support-quill-video" src="${url}"></video></p>`
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !quillRef.current) return;
    try {
      const res = await uploadAndInsert(file, 'file');
      if (!res?.url) return;
      const url = resolveUploadAssetUrl(res.url);
      const name = (res.filename || file.name || 'Attachment').replace(/"/g, '');
      const quill = quillRef.current;
      const range = quill.getSelection(true);
      const index = range?.index ?? Math.max(0, quill.getLength() - 1);
      quill.clipboard.dangerouslyPasteHTML(
        index,
        `<p><a href="${url}" target="_blank" rel="noopener noreferrer" class="support-quill-attachment">${name}</a></p>`
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div
      className={cn(
        'support-quill-editor flex flex-col bg-white dark:bg-slate-900',
        expanded ? 'support-quill-editor--expanded' : 'support-quill-editor--compact'
      )}
    >
      <div className="shrink-0 flex items-center gap-1 px-2 py-1 border-b border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/40">
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-slate-600">
              <Smile className="w-4 h-4 mr-1" />
              Emoji
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
            <div className="grid grid-cols-8 gap-0.5">
              {SUPPORT_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="text-lg hover:bg-slate-100 dark:hover:bg-slate-800 rounded p-1"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        {!orgId ? (
          <span className="text-[11px] text-amber-600 ml-auto">Uploads need a workspace</span>
        ) : null}
      </div>

      <div ref={hostRef} className="support-quill-host" data-testid="support-message-editor" />

      <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
      <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} />
      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
    </div>
  );
});

export default SupportMessageEditor;
